import type { z } from "zod";
import type { MigrationContext } from "./types.ts";

export class MigrationRegistryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MigrationRegistryError";
  }
}

export type RegisteredSchema = {
  version: number;
  schema: z.ZodType<unknown>;
};

export type RegisteredStep = {
  id: string;
  fromVersion: number;
  toVersion: number;
  description: string;
  migrate: (source: unknown, context: MigrationContext) => unknown;
};

export type MigrationRegistry = {
  currentVersion: number;
  minSupportedVersion: number;
  schemas: ReadonlyMap<number, z.ZodType<unknown>>;
  stepsByFromVersion: ReadonlyMap<number, RegisteredStep>;
  steps: readonly RegisteredStep[];
};

function requirePositiveInteger(value: number, label: string): void {
  if (!Number.isInteger(value) || value < 1) {
    throw new MigrationRegistryError(`${label} must be a positive integer.`);
  }
}

export function createMigrationRegistry(input: {
  currentVersion: number;
  schemas: readonly RegisteredSchema[];
  steps: readonly RegisteredStep[];
}): MigrationRegistry {
  requirePositiveInteger(input.currentVersion, "currentVersion");
  if (input.schemas.length === 0) {
    throw new MigrationRegistryError("A migration registry needs at least one schema.");
  }

  const schemas = new Map<number, z.ZodType<unknown>>();
  for (const entry of input.schemas) {
    requirePositiveInteger(entry.version, "schema version");
    if (schemas.has(entry.version)) {
      throw new MigrationRegistryError(`Duplicate schema registration for version ${entry.version}.`);
    }
    schemas.set(entry.version, entry.schema);
  }

  if (!schemas.has(input.currentVersion)) {
    throw new MigrationRegistryError(`Missing schema for current version ${input.currentVersion}.`);
  }

  const versions = [...schemas.keys()].sort((left, right) => left - right);
  const minSupportedVersion = versions[0]!;
  if (versions[versions.length - 1] !== input.currentVersion) {
    throw new MigrationRegistryError("Registered schemas must end at the current version.");
  }
  for (let version = minSupportedVersion; version <= input.currentVersion; version += 1) {
    if (!schemas.has(version)) {
      throw new MigrationRegistryError(
        `Gap in claimed supported schemas: missing version ${version}.`,
      );
    }
  }

  const stepIds = new Set<string>();
  const stepsByFromVersion = new Map<number, RegisteredStep>();
  for (const step of input.steps) {
    if (!step.id.trim()) {
      throw new MigrationRegistryError("Each migration step needs a stable non-empty id.");
    }
    if (stepIds.has(step.id)) {
      throw new MigrationRegistryError(`Duplicate migration step id “${step.id}”.`);
    }
    stepIds.add(step.id);
    requirePositiveInteger(step.fromVersion, "step fromVersion");
    requirePositiveInteger(step.toVersion, "step toVersion");
    if (step.toVersion !== step.fromVersion + 1) {
      throw new MigrationRegistryError(
        `Step “${step.id}” must migrate n → n+1 (got ${step.fromVersion} → ${step.toVersion}).`,
      );
    }
    if (stepsByFromVersion.has(step.fromVersion)) {
      throw new MigrationRegistryError(
        `Duplicate migration step from version ${step.fromVersion}.`,
      );
    }
    stepsByFromVersion.set(step.fromVersion, step);
  }

  for (let version = minSupportedVersion; version < input.currentVersion; version += 1) {
    if (!stepsByFromVersion.has(version)) {
      throw new MigrationRegistryError(
        `Gap in claimed supported paths: missing step from version ${version}.`,
      );
    }
  }
  for (const step of input.steps) {
    if (step.fromVersion < minSupportedVersion || step.toVersion > input.currentVersion) {
      throw new MigrationRegistryError(
        `Step “${step.id}” is outside the supported version range ${minSupportedVersion}–${input.currentVersion}.`,
      );
    }
  }

  return {
    currentVersion: input.currentVersion,
    minSupportedVersion,
    schemas,
    stepsByFromVersion,
    steps: [...input.steps],
  };
}

export function stepsFrom(registry: MigrationRegistry, sourceVersion: number): RegisteredStep[] {
  const steps: RegisteredStep[] = [];
  for (let version = sourceVersion; version < registry.currentVersion; version += 1) {
    const step = registry.stepsByFromVersion.get(version);
    if (!step) {
      return [];
    }
    steps.push(step);
  }
  return steps;
}
